<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SearchBar from '@/sites/tfs/components/public/SearchBar.vue';

type Props = {
  modelValue: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'close'): void;
}>();

const { t } = useI18n();
const modalRef = ref<HTMLElement | null>(null);
const closeButtonRef = ref<HTMLButtonElement | null>(null);
const searchBarRef = ref<{ focusInput: () => void } | null>(null);
let previousActiveElement: HTMLElement | null = null;

const closeModal = (): void => {
  emit('update:modelValue', false);
  emit('close');
};

const getFocusableElements = (): HTMLElement[] => {
  if (!modalRef.value) {
    return [];
  }

  return Array.from(
    modalRef.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden'));
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (!props.modelValue) {
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    closeModal();
    return;
  }

  if (event.key !== 'Tab') {
    return;
  }

  const focusableElements = getFocusableElements();
  if (focusableElements.length === 0) {
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = document.activeElement;

  if (event.shiftKey && activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
    return;
  }

  if (!event.shiftKey && activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
};

const focusSearchInput = (): void => {
  searchBarRef.value?.focusInput();
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (typeof document === 'undefined') {
      return;
    }

    if (isOpen) {
      previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeydown);
      void nextTick(() => {
        focusSearchInput();
        if (!document.activeElement || document.activeElement === document.body) {
          closeButtonRef.value?.focus();
        }
      });
      return;
    }

    document.body.style.overflow = '';
    document.removeEventListener('keydown', handleKeydown);
    previousActiveElement?.focus();
    previousActiveElement = null;
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (typeof document === 'undefined') {
    return;
  }

  document.body.style.overflow = '';
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 pt-[12vh] backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-modal-title"
        @click.self="closeModal"
      >
        <div ref="modalRef" class="w-full max-w-2xl rounded-xl border border-noir-700 bg-noir-900 p-4 shadow-2xl sm:p-6">
          <div class="mb-4 flex items-center justify-between gap-4">
            <h2 id="search-modal-title" class="text-lg font-semibold text-cream-100">
              {{ t('common.search') }}
            </h2>
            <button
              ref="closeButtonRef"
              type="button"
              :aria-label="t('common.close')"
              class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-noir-200 transition-colors hover:bg-noir-800 hover:text-cream-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-noir-900"
              @click="closeModal"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path d="M6 6L18 18M6 18L18 6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>

          <SearchBar
            ref="searchBarRef"
            autofocus
            size="large"
            @navigated="closeModal"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
